import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./CControlHistory.css";
import HistoryMap from "./HistoryMap.jsx";

import CircularProgressAnalytics from "./CircularProgressAnalytics.jsx";
import iconLogo from "../../assets/icon_trucktronix.png";
import imageTruck from "../../assets/american_axle_3_dog_trailer_3.png";
import { serviceVehicleGetDetails } from "../../api/serviceFleetMaster";

// Main Component
export default function CControlHistory() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    vehicle,
    fleetData: fleetDataArray = [], //todo ujj
    latLngArray = [],
  } = location.state || {};

  // mobile widget drawer: "truck" | "weight" | "state" | "faults" | null
  const [mobileWidget, setMobileWidget] = useState(null);
  //playback
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [tracking, setTracking] = useState(true);
  //for fault analytics
  const [vehicleLimits, setVehicleLimits] = useState(null);

  // Refs for map
  const mapRefRoute = useRef(null);
  const mapRefTrack = useRef(null);

  // Data comes from navigate(): fleetData and latLngArray are oldest first.
  const totalData = fleetDataArray.length;

  // Playback as smooth
  const {
    position: smoothPosition,
    bearing: smoothBearing,
    uiIndex: currentIndex,
    seekTo,
  } = useSmoothPlayback(fleetDataArray, isPlaying, playbackSpeed, setIsPlaying);

  const currentData = fleetDataArray[currentIndex] || null;
  const firstCoordinates = fleetDataArray[0]?.location?.coordinates;
  const currentPosition =
    smoothPosition ||
    (Array.isArray(firstCoordinates)
      ? [firstCoordinates[1], firstCoordinates[0]]
      : latLngArray[0] || null);

  const handleStreetView = useCallback(() => {
    if (!currentPosition) return;
    const [lat, lng] = currentPosition;
    window.open(
      `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`,
      "_blank",
      "noopener,noreferrer",
    );
  }, [currentPosition]);

  // Fetch vehicle limits
  useEffect(() => {
    if (!vehicle?._id) return;

    const fetchVehicleLimits = async () => {
      try {
        const data = await serviceVehicleGetDetails(
          vehicle?._id,
        );

        setVehicleLimits({
          steerMax: data.weightsMax?.steer,
          driveMax: data.weightsMax?.drive,
          trailerAMax: data.weightsMax?.trailerA,
          trailerBMax: data.weightsMax?.trailerB,
          imageUrl: data.imageUrl,
        });
      } catch (error) {
        console.warn("Failed to fetch vehicle limits:", error);
      }
    };

    fetchVehicleLimits();
  }, [vehicle?._id]);

  // Map controls
  const handleZoomIn = useCallback(() => mapRefRoute.current?.zoomIn(), []);
  const handleZoomOut = useCallback(() => mapRefRoute.current?.zoomOut(), []);

  const toggleWidget = useCallback(
    (key) => {
      if (mobileWidget === key) {
        // If the widget is already active, reset it immediately
        setMobileWidget(null);
        return;
      }

      // Reset the widget immediately
      setMobileWidget(null);

      // Add a delay to allow for smooth animation reset before setting the new widget
      setTimeout(() => {
        setMobileWidget(key);
      }, 150); // Adjust the delay as needed for smooth transition
    },
    [mobileWidget],
  );

  // Empty state
  if (!location.state || totalData === 0) {
    return (
      <div className="cch-root cch-root-empty">
        <div className="cch-card-empty cch-card">
          <div className="cch-card-empty-icon">
            <i className="bi bi-truck" />
          </div>
          <h1>No Playback Data</h1>
          <p>
            Please select a vehicle and time range to view history playback.
          </p>
          <button className="cch-btn-empty" onClick={() => navigate(-1)}>
            <i className="bi bi-chevron-double-left" /> Back to History
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cch-root">
      {/* Logo */}
      <img src={iconLogo} alt="trucktronix" className="cch-logo" />

      {/* Top Navigation buttons */}
      <div className="cch-buttons-navigation">
        <button className="cch-btn-back" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left" />
          <span>Back</span>
        </button>

        <div className="cch-buttons-mobile">
          <button
            className={`cch-btn-mobile ${mobileWidget === "truck" ? "active" : ""}`}
            onClick={() => toggleWidget("truck")}
            title="Truck Details"
          >
            <i className="bi bi-truck" />
          </button>

          <button
            className={`cch-btn-mobile ${mobileWidget === "weight" ? "active" : ""}`}
            onClick={() => toggleWidget("weight")}
            title="Weight Details"
          >
            <i className="bi bi-speedometer2" />
          </button>

          <button
            className={`cch-btn-mobile ${mobileWidget === "state" ? "active" : ""}`}
            onClick={() => toggleWidget("state")}
            title="Truck State"
          >
            <i className="bi bi-clipboard-data" />
          </button>

          <button
            className={`cch-btn-mobile ${mobileWidget === "faults" ? "active" : ""}`}
            onClick={() => toggleWidget("faults")}
            title="Fault Details"
          >
            <i className="bi bi-exclamation-triangle" />
          </button>
        </div>
      </div>

      {/* Map buttons */}
      <div className="cch-buttons-map">
        <button
          className={`cch-btn-map ${tracking ? "active" : ""}`}
          onClick={() => setTracking((v) => !v)}
          title="Track vehicle"
        >
          <i className="bi bi-crosshair" />
        </button>
        <button className="cch-btn-map" onClick={handleZoomIn} title="Zoom In">
          <i className="bi bi-plus" />
        </button>
        <button
          className="cch-btn-map"
          onClick={handleZoomOut}
          title="Zoom Out"
        >
          <i className="bi bi-dash" />
        </button>
      </div>

      {/* Main map: custom MapLibre code lives in HistoryMap.jsx. */}
      <div className="cch-map-container">
        <HistoryMap
          variant="main"
          mapRef={mapRefRoute}
          latLngArray={latLngArray}
          fleetData={fleetDataArray}
          currentIndex={currentIndex}
          position={currentPosition}
          bearing={smoothBearing}
          status={currentData?.status}
          isPlaying={isPlaying}
          tracking={tracking}
          onTrackingChange={setTracking}
        />
      </div>

      {/* Side Panel */}
      <aside className="cch-side-panel">
        <div className="cch-track-map">
          <HistoryMap
            variant="mini"
            mapRef={mapRefTrack}
            latLngArray={latLngArray}
            fleetData={fleetDataArray}
            currentIndex={currentIndex}
            position={currentPosition}
            bearing={smoothBearing}
            status={currentData?.status}
            isPlaying={isPlaying}
            tracking={tracking}
            onTrackingChange={setTracking}
          />
          <div className="cch-address-bar">
            <i className="bi bi-geo-alt" />
            <span>{currentData?.address || "-"}</span>
            <button
              className="cch-streetview-btn"
              onClick={handleStreetView}
              title="Street View"
            >
              <i className="bi bi-eye" />
            </button>
          </div>
        </div>
      </aside>

      {/* Desktop-only floating fault analytics (hidden on mobile). */}
      <div className="cch-analytics-float">
        {/* <AnalyticsWidget
          fleetDataArray={fleetDataArray}
          vehicleLimits={vehicleLimits}
          currentIndex={currentIndex}
          totalData={totalData}
        /> */}
      </div>

      {/* Mobile Widget Drawer */}
      <div
        className={`cch-container-mobile cch-card ${mobileWidget ? "open" : ""}`}
      >
        {mobileWidget === "truck" && (
          <section className="cch-card cch-widget">
            <DetailsMobile fleetData={currentData} vehicle={vehicle} />
            {/* <CurrentPlaybackInfo fleetData={currentData} /> 
            <VehicleDetails fleetData={currentData} /> */}
          </section>
        )}
        {mobileWidget === "weight" && <WeightWidget fleetData={currentData} />}
        {mobileWidget === "state" && (
          <TruckStateWidget fleetData={currentData} />
        )}
        {mobileWidget === "faults" && (
          <AnalyticsWidget
            fleetDataArray={fleetDataArray}
            vehicleLimits={vehicleLimits}
            currentIndex={currentIndex}
            totalData={totalData}
          />
        )}
      </div>

      {/* Player Controls */}
      <footer className="cch-footer-panel">
        <WeightWidget fleetData={currentData} />

        <div className="cch-player-panel cch-card">
          <div className="cch-player-left">
            <VehicleDetails vehicle={vehicle} fleetData={currentData} />
          </div>

          <div className="cch-divider" />

          <div className="cch-player-center">
            <PlaybackControls
              mapRef={mapRefRoute}
              fleetDataArray={fleetDataArray}
              currentIndex={currentIndex}
              seekTo={seekTo}
              setTracking={setTracking}
              isPlaying={isPlaying}
              setIsPlaying={setIsPlaying}
              playbackSpeed={playbackSpeed}
              setPlaybackSpeed={setPlaybackSpeed}
              onScrub={() => setTracking(false)}
            />
          </div>

          <div className="cch-divider" />

          <div className="cch-player-right">
            <CurrentPlaybackInfo fleetData={currentData} vehicle={vehicle} />
          </div>
        </div>

        <TruckStateWidget fleetData={currentData} />
      </footer>
    </div>
  );
}

/* =========================================================
   SMOOTH PLAYBACK Hook
========================================================= */
function useSmoothPlayback(
  fleetDataArray,
  isPlaying,
  playbackSpeed,
  setIsPlaying,
) {
  const rafRef = useRef(null);

  const [position, setPosition] = useState(null);
  const [bearing, setBearing] = useState(0);
  const [uiIndex, setUiIndex] = useState(0);

  const segmentRef = useRef({
    index: 0,
    startTime: 0,
    from: null,
    to: null,
  });

  const stepMs = 1000 / Math.max(0.25, playbackSpeed);
  const ease = (t) => t * t * (3 - 2 * t);

  const seekTo = useCallback(
    (nextIndex) => {
      const max = Math.max((fleetDataArray?.length || 1) - 1, 0);
      const i = Math.max(0, Math.min(max, Number(nextIndex) || 0));

      segmentRef.current.index = i;
      setUiIndex(i);

      const curr = fleetDataArray[i];
      if (!curr?.location?.coordinates) return;

      const [currLon, currLat] = curr?.location?.coordinates;
      const snap = [currLat, currLon];

      segmentRef.current.from = snap;

      const next = fleetDataArray[i + 1];
      let to = snap;

      if (next?.location?.coordinates) {
        const [nextLon, nextLat] = next?.location?.coordinates;
        to = [nextLat, nextLon];
      }

      segmentRef.current.to = to;
      segmentRef.current.startTime = performance.now();

      setPosition(snap);

      if (next?.location?.coordinates) {
        setBearing(computeBearing(snap[0], snap[1], to[0], to[1]));
      }
    },
    [fleetDataArray],
  );

  useEffect(() => {
    seekTo(0);
  }, [seekTo]);

  useEffect(() => {
    if (!isPlaying) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }

    const tick = (now) => {
      const { index, startTime, from, to } = segmentRef.current;

      const a = fleetDataArray[index];
      if (!a?.location?.coordinates) {
        rafRef.current = null;
        setIsPlaying(false);
        return;
      }
      const [aLon, aLat] = a.location.coordinates;

      const b = fleetDataArray[index + 1];
      if (!b?.location?.coordinates) {
        // No next sample: stop playback instead of leaving Play active.
        rafRef.current = null;
        setIsPlaying(false);
        return;
      }
      const [bLon, bLat] = b.location.coordinates;

      // if (!a) {
      //   rafRef.current = requestAnimationFrame(tick);
      //   return;
      // }

      // if (!b) {
      //   const snap = [aLat, aLon];
      //   setPosition(snap);
      //   setBearing(0);
      //   rafRef.current = null;
      //   return;
      // }

      const currentFrom = from || [aLat, aLon];
      const currentTo = to || [bLat, bLon];

      const t = Math.min((now - startTime) / Math.max(16, stepMs), 1);
      const e = ease(t);

      const p = [
        currentFrom[0] + (currentTo[0] - currentFrom[0]) * e,
        currentFrom[1] + (currentTo[1] - currentFrom[1]) * e,
      ];

      setPosition(p);
      setBearing(
        computeBearing(
          currentFrom[0],
          currentFrom[1],
          currentTo[0],
          currentTo[1],
        ),
      );

      if (t >= 1) {
        const nextIdx = index + 1;

        if (nextIdx >= fleetDataArray.length - 1) {
          // End at the last GPS fix. Don't jump back to the beginning: the
          // highlighted progress should remain visible until the user restarts.
          if (rafRef.current) cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
          segmentRef.current.index = nextIdx;
          setUiIndex(nextIdx);
          setPosition(currentTo);
          setIsPlaying(false);
          return;
        }

        segmentRef.current.index = nextIdx;
        setUiIndex(nextIdx);
        segmentRef.current.from = currentTo;

        const c = fleetDataArray[nextIdx + 1];
        if (c?.location?.coordinates) {
          const [cLon, cLat] = c.location.coordinates;
          segmentRef.current.to = [cLat, cLon];
        } else {
          segmentRef.current.to = currentTo;
        }

        segmentRef.current.startTime = now;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [isPlaying, stepMs, fleetDataArray]);

  return { position, bearing, uiIndex, seekTo };
}

// Math Utility
function computeBearing(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lon2 - lon1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

// Widget Components
function PlaybackControls({
  mapRef,
  fleetDataArray,
  currentIndex,
  seekTo,
  setTracking,
  isPlaying,
  setIsPlaying,
  playbackSpeed,
  setPlaybackSpeed,
  onScrub,
}) {
  // useEffect(() => {
  //   if (!isPlaying) return;

  //   const map = mapRef.current;
  //   if (!map) return;

  //   map.easeTo({
  //     zoom: Math.max(map.getZoom(), 20),
  //     duration: 2000,
  //     essential: true,
  //   });
  // }, [isPlaying, mapRef]);
  useEffect(() => {
    if (!isPlaying) return;

    mapRef.current?.zoomTo(18);
  }, [isPlaying, mapRef]);

  const total = fleetDataArray.length;
  const progress =
    total > 1 ? Math.round((currentIndex / (total - 1)) * 100) : 0;

  const handleStep = (dir) => {
    setIsPlaying(false);
    seekTo(currentIndex + dir);
  };

  const speeds = [0.5, 1, 2, 4];

  const decreaseSpeed = () => {
    const idx = speeds.indexOf(playbackSpeed);
    if (idx > 0) {
      setPlaybackSpeed(speeds[idx - 1]);
    }
  };

  const increaseSpeed = () => {
    const idx = speeds.indexOf(playbackSpeed);
    if (idx < speeds.length - 1) {
      setPlaybackSpeed(speeds[idx + 1]);
    }
  };

  return (
    <div className="cch-playback-container">
      <div className="cch-playback-header">
        <div className="cch-progress-info">
          {currentIndex + 1}/{total} · {progress}%
        </div>
        <div className="cch-speed-control">
          <label>SPEED</label>
          <select
            value={playbackSpeed}
            onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
            className="cch-speed-select"
          >
            <option value={0.5}>0.5×</option>
            <option value={1}>1×</option>
            <option value={2}>2×</option>
            <option value={4}>4×</option>
          </select>
        </div>
      </div>

      <div className="cch-playback-buttons">
        <button
          className="cch-playback-btn"
          onClick={() => handleStep(-1)}
          disabled={currentIndex === 0}
          title="Previous"
        >
          <i className="bi bi-skip-start-fill" />
        </button>

        {/* slower */}
        <button
          className="cch-playback-btn"
          onClick={decreaseSpeed}
          disabled={playbackSpeed <= speeds[0]}
          title="Slower"
        >
          <i className="bi bi-rewind-fill" />
        </button>

        <button
          className="cch-playback-btn"
          onClick={() => {
            setIsPlaying(false);
            seekTo(0);
            setTracking(true);
          }}
          title="Go to start"
        >
          <i className="bi bi-stop-fill" />
        </button>

        <button
          className="cch-playback-btn primary"
          onClick={() => {
            setTracking(true);
            if (!isPlaying && currentIndex >= total - 1) seekTo(0);
            setIsPlaying((v) => !v);
          }}
          disabled={total < 2}
          title={isPlaying ? "Pause" : "Play"}
        >
          <i className={`bi bi-${isPlaying ? "pause-fill" : "play-fill"}`} />
        </button>

        {/* faster */}
        <button
          className="cch-playback-btn"
          onClick={increaseSpeed}
          disabled={playbackSpeed >= speeds[speeds.length - 1]}
          title="Faster"
        >
          <i className="bi bi-fast-forward-fill" />
        </button>

        <button
          className="cch-playback-btn"
          onClick={() => handleStep(1)}
          disabled={currentIndex >= total - 1}
          title="Next"
        >
          <i className="bi bi-skip-end-fill" />
        </button>
      </div>

      <div className="cch-playback-slider">
        <input
          type="range"
          min={0}
          max={Math.max(total - 1, 0)}
          value={currentIndex}
          onMouseDown={onScrub}
          onTouchStart={onScrub}
          onChange={(e) => {
            setIsPlaying(false);
            seekTo(Number(e.target.value));
            setTracking(true);
          }}
          className="cch-progress-slider"
          style={{ "--progress": `${progress}%` }}
        />
        <div className="cch-time-range">
          <span>{formatTimestamp(fleetDataArray[0]?.timestamp)}</span>
          <span>{formatTimestamp(fleetDataArray[total - 1]?.timestamp)}</span>
        </div>
      </div>
    </div>
  );
}

function AnalyticsWidget({
  fleetDataArray,
  vehicleLimits,
  currentIndex,
  totalData,
}) {
  const faultCounts = useMemo(() => {
    const totals = { steer: 0, drive: 0, ta: 0, tb: 0 };
    if (!fleetDataArray.length || !vehicleLimits) return totals;

    const slice = fleetDataArray.slice(0, currentIndex + 1);
    slice.forEach((point) => {
      if (
        vehicleLimits.steerMax &&
        point.weights?.steer > vehicleLimits.steerMax
      )
        totals.steer++;
      if (
        vehicleLimits.driveMax &&
        point.weights?.drive > vehicleLimits.driveMax
      )
        totals.drive++;
      if (
        vehicleLimits.trailerAMax &&
        point.weights?.trailerA > vehicleLimits.trailerAMax
      )
        totals.ta++;
      if (
        vehicleLimits.trailerBMax &&
        point.weights?.trailerB > vehicleLimits.trailerBMax
      )
        totals.tb++;
    });
    return totals;
  }, [fleetDataArray, vehicleLimits, currentIndex]);

  const hasFaults = Object.values(faultCounts).some((count) => count > 0);

  return (
    <section className="cch-card cch-widget">
      <div className="cch-widget-header">
        <i className="bi bi-exclamation-triangle" />
        <span>Fault Analytics</span>
      </div>
      <div className="cch-container-analytics">
        {/* <CircularProgressAnalytics label="Steer" value={faultCounts.steer} total={totalData} color="#ef4444" />
        <CircularProgressAnalytics label="Drive" value={faultCounts.drive} total={totalData} color="#f59e0b" />
        <CircularProgressAnalytics label="Trailer A" value={faultCounts.ta} total={totalData} color="#3b82f6" />
        <CircularProgressAnalytics label="Trailer B" value={faultCounts.tb} total={totalData} color="#10b981" /> */}

        <CircularProgressAnalytics
          label="Steer"
          value={faultCounts.steer}
          total={totalData}
          color="#f59e0b"
        />
        <CircularProgressAnalytics
          label="Drive"
          value={faultCounts.drive}
          total={totalData}
          color="#f59e0b"
        />
        <CircularProgressAnalytics
          label="Trailer A"
          value={faultCounts.ta}
          total={totalData}
          color="#f59e0b"
        />
        <CircularProgressAnalytics
          label="Trailer B"
          value={faultCounts.tb}
          total={totalData}
          color="#f59e0b"
        />
      </div>
    </section>
  );
}

function TruckStateWidget({ fleetData }) {
  const truckStates = [
    {
      key: "isTailGateOpen",
      label: "Tail Gate",
      iconOn: "bi-door-open",
      iconOff: "bi-door-closed",
      critical: false,
    },
    {
      key: "isTarpOn",
      label: "Tarp",
      iconOn: "bi-shield-check",
      iconOff: "bi-shield-x",
      critical: false,
    },
    {
      key: "pto",
      label: "PTO",
      iconOn: "bi-gear-fill",
      iconOff: "bi-gear",
      critical: false,
    },
    {
      key: "isBinRaised",
      label: "Bin Raised",
      iconOn: "bi-truck-flatbed",
      iconOff: "bi-truck",
      critical: true,
    },
    {
      key: "isOverload",
      label: "Overload",
      iconOn: "bi-exclamation-octagon-fill",
      iconOff: "bi-check-circle",
      critical: true,
    },
  ];

  return (
    <section className="cch-state-widget cch-card cch-widget">
      <div className="cch-widget-header">
        <i className="bi bi-truck-front" />
        <span>Vehicle State</span>
      </div>

      <div className="cch-container-truck-state">
        {truckStates.map((state) => {
          var isActive = !!fleetData?.[state.key];

          if (state.key === "pto" || state.key === "isBinRaised") {
            isActive = true;
          }

          return (
            <div
              className={`cch-card-truck-state ${isActive ? "active" : ""} ${state.critical && isActive ? "danger" : ""}`}
              key={state.key}
            >
              <div className="icon-container">
                <i
                  className={`bi ${isActive ? state.iconOn : state.iconOff}`}
                />
              </div>
              <div className="state-info">
                <span className="label">{state.label}</span>
                <span
                  className={`value ${isActive ? "active" : ""} ${state.critical && isActive ? "danger" : ""}`}
                >
                  {isActive ? "ON" : "OFF"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function WeightWidget({ fleetData }) {
  const [showDetails, setShowDetails] = useState(true);
  if (!fleetData) return null;

  const formatValue = (v) => {
    if (v == null || Number.isNaN(Number(v))) return "-";
    return Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 });
  };

  const axleWeights = [
    { label: "Drive", value: fleetData.weights?.drive || 0 },
    ...(fleetData.weights?.steer > 0
      ? [{ label: "Steer", value: fleetData.weights?.steer }]
      : []),
    ...(fleetData.weights?.trailerA > 0
      ? [{ label: "Trailer A", value: fleetData.weights?.trailerA }]
      : []),
    ...(fleetData.weights?.trailerB > 0
      ? [{ label: "Trailer B", value: fleetData.weights?.trailerB }]
      : []),
  ];

  const total =
    (fleetData.weights?.drive || 0) +
    (fleetData.weights?.steer || 0) +
    (fleetData.weights?.trailerA || 0) +
    (fleetData.weights?.trailerB || 0);

  const getPercentage = (v) =>
    total > 0 ? ((v / total) * 100).toFixed(1) : "0.0";

  return (
    <section className="cch-weight-widget cch-card cch-widget">
      <div className="cch-widget-header">
        <i className="bi bi-speedometer2" />
        <span>Weight Distribution</span>
        <button
          className="cch-widget-header-btn"
          onClick={() => setShowDetails((v) => !v)}
          title={showDetails ? "Hide details" : "Show details"}
        >
          <i className={`bi bi-${showDetails ? "eye-slash" : "eye"}`} />
        </button>
      </div>

      {showDetails && (
        <div className="cch-weight-details">
          {axleWeights.map((axle) => (
            <div className="cch-weight-row" key={axle.label}>
              <div className="cch-weight-label">{axle.label}</div>
              <div className="cch-weight-bar">
                <div
                  className="cch-weight-fill"
                  style={{ width: `${getPercentage(axle.value)}%` }}
                />
              </div>
              <div className="cch-weight-value">
                <span>{getPercentage(axle.value)}%</span>
                <span>
                  {formatValue(axle.value)} {fleetData.scaleUnit || "kg"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="cch-weight-total">
        <span>Total Weight</span>
        <span className="cch-total-value">
          {formatValue(total)} {fleetData.scaleUnit || "kg"}
        </span>
      </div>
    </section>
  );
}

function DetailsMobile({ fleetData, vehicle }) {
  if (!fleetData) return null;
  const { date, time } = formatDateTime(fleetData.timestamp);

  return (
    <div className="cch-container-dm">
      {/* Left: Truck Visual & ID */}
      <div className="cch-image-dm">
        <img src={imageTruck} alt="Truck" />
        <div className="cch-vehicle-dm">
          {vehicle?.vehicleCode || "Unknown"}
        </div>
      </div>

      {/* Right: Operational Data */}
      <div className="cch-content-dm">
        <div className="cch-info-dm">
          <span className="cch-label-dm">
            <i className="bi bi-person cch-icon-dm" />
            Driver
          </span>
          <span className="cch-value-dm">
            {vehicle.employee?.name || "N/A"}
          </span>
        </div>

        <div className="cch-timestamp-dm">
          <i className="bi bi-clock-history" />
          <span>{time}</span>
          <span className="cch-dot-dm"></span>
          <span>{date}</span>
        </div>
      </div>
    </div>
  );
}

function VehicleDetails({ vehicle, fleetData }) {
  if (!fleetData || !vehicle) return null;

  return (
    <div className="cch-vehicle-details">
      <div className="cch-vehicle-info">
        <div className="cch-info-row">
          <span className="cch-info-label">Registration</span>
          <span className="cch-info-value">{vehicle.vehicleCode || "N/A"}</span>
        </div>
        <div className="cch-info-row">
          <span className="cch-info-label">Driver</span>
          <span className="cch-info-value">
            {vehicle.employee?.name || "N/A"}
          </span>
        </div>
      </div>
      <img src={imageTruck} alt="Truck" className="cch-vehicle-image" />
    </div>
  );
}

function CurrentPlaybackInfo({ fleetData, vehicle }) {
  if (!fleetData) return null;
  const { date, time } = formatDateTime(fleetData.timestamp);

  return (
    <div className="cch-current-container">
      <div className="cch-current-title">
        <i className="bi bi-clock cch-current-icon" />
        <span className="cch-current-label">PLAYBACK</span>
      </div>
      <div className="cch-current-timestamp">
        <span className="cch-current-date">{date}</span>
        <span className="cch-current-dot" />
        <span className="cch-current-time">{time}</span>
      </div>
    </div>
  );
}

// Utility
const formatTimestamp = (iso) => {
  if (!iso) return "-";
  const date = new Date(iso);
  return date.toLocaleString();
};

const formatDateTime = (iso) => {
  if (!iso) return { date: "-", time: "-" };

  const dateObj = new Date(iso);

  return {
    date: dateObj.toLocaleDateString(),
    time: dateObj.toLocaleTimeString(),
  };
};
